/**
 * Live equation breakdown bound to glossary + current SimParams.
 * Dense by default: headline + live values always on;
 * symbols collapsed; operations + interactions open under details.
 */

import {
  EQUATION_GLOSSARY,
  getParamHelp,
  type EquationGlossary,
} from "../help/glossary";
import type { GuideMode } from "../help/guideModes";
import type { SimParams } from "../sim/params";

function fmt(n: number, digits = 3): string {
  if (Number.isInteger(n)) return String(n);
  return n
    .toFixed(digits)
    .replace(/\.?0+$/, (m) => (m.startsWith(".") ? "" : m));
}

function liveValue(paramId: string | null, sim: SimParams): string | null {
  if (!paramId) return null;
  switch (paramId) {
    case "contraction":
      return fmt(sim.contraction, 4);
    case "friend_step":
      return fmt(sim.friendStep, 4);
    case "enemy_step":
      return fmt(sim.enemyStep, 4);
    case "softening":
      return fmt(sim.softening, 4);
    case "rechoose_prob":
      return fmt(sim.rechooseProb, 2);
    case "topology":
      return sim.topology;
    default:
      return null;
  }
}

export class EquationPanel {
  private readonly root: HTMLElement;
  private readonly g: EquationGlossary = EQUATION_GLOSSARY;
  private built = false;
  private readonly termValEls = new Map<string, HTMLElement>();
  private symbolsDetails: HTMLDetailsElement | null = null;
  private opsDetails: HTMLDetailsElement | null = null;
  private ixDetails: HTMLDetailsElement | null = null;

  constructor(host: HTMLElement) {
    this.root = host;
    this.root.classList.add("equation-panel");
    this.root.innerHTML = "";
    this.buildChrome();
  }

  private buildChrome(): void {
    if (this.built) return;
    this.built = true;

    const title = el("h2", "Equation");
    title.id = "equation-heading";

    const headline = el("p", "", "eq");
    headline.id = "equation-hud";
    headline.innerHTML = this.g.headline_html;

    const secondary = el("p", "", "eq eq-secondary");
    secondary.innerHTML = this.g.secondary.headline_html;

    const live = el("p", "", "eq-detail");
    live.id = "equation-detail";

    const intro = el("p", this.g.intro, "eq-intro");

    // Symbols — collapsed by default (dense); expand for teaching
    const termsDetails = document.createElement("details");
    termsDetails.className = "eq-fold";
    termsDetails.id = "equation-terms-fold";
    termsDetails.append(el("summary", "Symbols"));
    const termsHost = el("div", "", "eq-terms");
    termsHost.id = "equation-terms";
    for (const t of this.g.terms) {
      const row = el("div", "", "eq-term");
      const sym = el("span", t.symbol, "eq-term-sym");
      const body = el("div", "", "eq-term-body");
      const nameRow = el("div", "", "eq-term-name");
      nameRow.append(el("strong", t.name));
      if (t.param) {
        const valEl = el("em", "—", "eq-term-val");
        valEl.dataset.termParam = t.param;
        this.termValEls.set(t.id, valEl);
        nameRow.append(valEl);
      }
      body.append(nameRow, el("p", t.meaning, "eq-term-meaning"));
      if (t.param) {
        body.append(
          el(
            "p",
            `Slider: ${getParamHelp(t.param)?.short ?? t.param}`,
            "eq-term-link",
          ),
        );
      }
      row.append(sym, body);
      termsHost.append(row);
    }
    termsDetails.append(termsHost);
    this.symbolsDetails = termsDetails;

    // Operations — valuable; open in guided/lab
    const opsDetails = document.createElement("details");
    opsDetails.className = "eq-fold";
    opsDetails.id = "equation-ops-fold";
    opsDetails.append(el("summary", "Operations"));
    const opsHost = el("div", "", "eq-ops");
    for (const op of this.g.operations) {
      const row = el("div", "", "eq-op-row");
      row.append(
        el("code", op.label, "eq-op-label"),
        el("span", op.meaning, "eq-op-meaning"),
      );
      opsHost.append(row);
    }
    opsDetails.append(opsHost);
    this.opsDetails = opsDetails;

    // Interactions — valuable; open in guided/lab
    const ixDetails = document.createElement("details");
    ixDetails.className = "eq-fold";
    ixDetails.id = "equation-interactions-fold";
    ixDetails.append(el("summary", "How pieces interact"));
    const interactHost = el("div", "", "eq-interactions");
    for (const ix of this.g.interactions) {
      const card = el("div", "", "eq-ix");
      card.append(el("strong", ix.title), el("p", ix.body));
      interactHost.append(card);
    }
    ixDetails.append(interactHost);
    this.ixDetails = ixDetails;

    this.root.append(
      title,
      headline,
      secondary,
      live,
      intro,
      termsDetails,
      opsDetails,
      ixDetails,
    );
  }

  applyGuide(mode: GuideMode): void {
    const teach = mode.showEquationBreakdown;
    this.root.classList.toggle("eq-compact", !teach);
    qs(this.root, ".eq-intro")?.classList.toggle("hidden", !teach);
    qs(this.root, ".eq-secondary")?.classList.toggle(
      "hidden",
      mode.id === "compact",
    );

    if (this.symbolsDetails) {
      this.symbolsDetails.classList.toggle("hidden", !teach);
      // Symbols stay collapsed unless user opens — density first
      if (!teach) this.symbolsDetails.open = false;
    }
    if (this.opsDetails) {
      this.opsDetails.classList.toggle("hidden", !teach);
      // Ops + interactions default open in teaching modes
      this.opsDetails.open = teach;
    }
    if (this.ixDetails) {
      const showIx = mode.showInteractions;
      this.ixDetails.classList.toggle("hidden", !showIx);
      this.ixDetails.open = showIx;
    }
  }

  update(sim: SimParams): void {
    const live = qs(this.root, "#equation-detail");
    if (live) {
      const topoNote = topologyReason(sim.topology);
      const motionOn =
        sim.motionMode === "inertial" ||
        (sim.gravityMode && sim.gravityMode !== "none");
      const motionLine = motionOn
        ? `\nmotion=${sim.motionMode}` +
          (sim.motionMode === "inertial"
            ? `  δ=${fmt(sim.velocityDamping, 2)}  α=${fmt(sim.socialGain, 2)}`
            : "") +
          (sim.gravityMode && sim.gravityMode !== "none"
            ? `  g=${sim.gravityMode}·${fmt(sim.gravityStrength, 4)}` +
              `  m=${fmt(sim.particleMass, 1)}` +
              `  (γ/m≈${fmt(sim.gravityStrength / Math.max(0.25, sim.particleMass), 4)})`
            : "")
        : "";
      live.textContent =
        `c=${fmt(sim.contraction, 4)}  pₛ=${fmt(sim.friendStep, 4)}  ` +
        `qₛ=${fmt(sim.enemyStep, 4)}  ε=${fmt(sim.softening, 4)}\n` +
        `r=${fmt(sim.rechooseProb, 2)}  model=${sim.model}  topology=${sim.topology}` +
        (sim.freezeTopology ? "  · frozen" : "") +
        motionLine +
        (topoNote ? `\n${topoNote}` : "");
    }

    for (const t of this.g.terms) {
      const valEl = this.termValEls.get(t.id);
      if (!valEl) continue;
      const v = liveValue(t.param, sim);
      if (v != null) valEl.textContent = v;
    }
  }
}

/**
 * Short teachable note: voxel presets share Woods algebra; what changes is
 * who is friend/enemy (topology) and where particles start (init), not the step.
 */
function topologyReason(topology: string): string {
  if (topology.startsWith("voxel_")) {
    return "Voxel: same Woods step; lattice neighbors set p/q (not a different equation).";
  }
  if (topology === "ring" || topology === "kirma_multiring") {
    return "Ring topology: same Woods step; friendship is a fixed cycle index chain.";
  }
  if (topology === "random" || topology === "random_distinct") {
    return "Free graph: same Woods step; p/q are random (rechoose may rewire).";
  }
  return "";
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  text = "",
  className = "",
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

function qs(root: ParentNode, sel: string): HTMLElement | null {
  return root.querySelector(sel);
}
